import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Globe, Check } from 'lucide-react';
import { forwardRef } from 'react';
import { cn } from './cn';

export interface LocaleOption {
  code: string;
  label: string;
}

export interface LocaleSwitcherProps {
  supportedLocales: ReadonlyArray<LocaleOption>;
  currentLocale: string;
  onChange: (locale: string) => void;
  className?: string;
  'data-testid'?: string;
}

export const LocaleSwitcher = forwardRef<HTMLButtonElement, LocaleSwitcherProps>(
  ({ supportedLocales, currentLocale, onChange, className, 'data-testid': testId }, ref) => (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger
        ref={ref}
        aria-label="언어 변경"
        data-testid={testId ? `${testId}-trigger` : undefined}
        className={cn(
          'inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors',
          'focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]',
          'disabled:pointer-events-none disabled:opacity-50',
          className,
        )}
        style={{
          color: 'var(--content-muted)',
          background: 'transparent',
        }}
        onMouseOver={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-1)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--content-strong)';
        }}
        onMouseOut={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--content-muted)';
        }}
      >
        <Globe style={{ width: '18px', height: '18px' }} />
      </DropdownMenuPrimitive.Trigger>

      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="end"
          sideOffset={6}
          data-testid={testId ? `${testId}-menu` : undefined}
          className={cn(
            'z-50 min-w-[9rem] overflow-hidden rounded-md p-1',
            'shadow-[var(--elevation-2)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
          )}
          style={{
            background: 'var(--surface-raised)',
            border: '1px solid var(--divider-strong)',
            color: 'var(--content-strong)',
          }}
        >
          {supportedLocales.map((locale) => {
            const isSelected = locale.code === currentLocale;
            return (
              <DropdownMenuPrimitive.Item
                key={locale.code}
                aria-current={isSelected ? 'true' : undefined}
                data-testid={testId ? `${testId}-item-${locale.code.slice(0, 2).toLowerCase()}` : undefined}
                onSelect={() => onChange(locale.code)}
                className={cn(
                  'relative flex cursor-pointer select-none items-center justify-between rounded-sm px-3 py-2 text-sm outline-none transition-colors',
                  'focus:bg-[var(--surface-1)] data-[highlighted]:bg-[var(--surface-1)]',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                )}
                style={{
                  color: isSelected ? 'var(--accent-indigo)' : 'var(--content-strong)',
                  fontWeight: isSelected ? 600 : 400,
                }}
              >
                <span>{locale.label}</span>
                {isSelected && (
                  <Check style={{ width: '14px', height: '14px', flexShrink: 0 }} />
                )}
              </DropdownMenuPrimitive.Item>
            );
          })}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  ),
);
LocaleSwitcher.displayName = 'LocaleSwitcher';
